import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocalizationPipe } from '@abp/ng.core';
import { Confirmation } from '@abp/ng.theme.shared';
import { EXTENSIONS_FORM_PROP_DATA } from '@abp/ng.components/extensible';
import { IdentityUserDto } from '@volo/abp.ng.identity/proxy';
import { TooltipModule } from 'primeng/tooltip';
import { EMPTY, finalize, switchMap } from 'rxjs';
import { ConfirmationService } from 'src/core/services/confirmation/confirmation.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { SipCredentialService } from 'src/app/proxy/sip-credential.service';
import type { SipDeviceCredentialDto } from 'src/app/proxy/dtos/agent-management/models';

/**
 * Read-only view of an agent's long-lived SIP device credentials, rendered inside the ABP user edit
 * form as a custom `FormProp` template.
 *
 * Read-only on purpose: the secret is generated with far more entropy than anyone would type, so the
 * only supported way to change it is to generate a new one. That also means this component never
 * participates in the surrounding form — nothing here is submitted with the user record, and the
 * secret is not an editable field that a stray Save could overwrite with an unusable value.
 *
 * Credentials are fetched only when asked for, never on open. The backend provisions a secret on read,
 * so loading eagerly would mint credentials for every agent whose record merely got looked at.
 */
@Component({
  selector: 'app-sip-credential-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, LocalizationPipe, TooltipModule],
  templateUrl: './sip-credential-panel.component.html',
  styleUrl: './sip-credential-panel.component.scss',
})
export class SipCredentialPanelComponent {
  private readonly sipCredentialService = inject(SipCredentialService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * The user record being edited. ABP provides the record itself under this token — not a wrapper —
   * via the injector it builds for a `template` form prop.
   */
  private readonly record = inject<IdentityUserDto | null>(
    EXTENSIONS_FORM_PROP_DATA,
    { optional: true }
  );

  readonly credentials = signal<SipDeviceCredentialDto | null>(null);
  readonly loading = signal(false);
  readonly rotating = signal(false);

  /**
   * Absent on the create form, where there is no user to read credentials for. The panel is registered
   * only as an edit-form contributor, so this is a guard rather than the normal path.
   */
  get userId(): string | null {
    return this.record?.id ?? null;
  }

  get busy(): boolean {
    return this.loading() || this.rotating();
  }

  show(): void {
    const userId = this.userId;
    if (!userId || this.busy) {
      return;
    }

    this.loading.set(true);
    this.sipCredentialService
      .get(userId)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: credentials => this.credentials.set(credentials),
        error: () => this.toaster.error('AgentManagement::Sip:LoadFailed'),
      });
  }

  hide(): void {
    this.credentials.set(null);
  }

  /**
   * Confirms first, because this invalidates every device registration the agent currently has — the
   * confirmation text says so explicitly rather than asking a generic "are you sure".
   */
  rotate(): void {
    const userId = this.userId;
    if (!userId || this.busy) {
      return;
    }

    this.confirmationService
      .warn(
        'AgentManagement::Sip:RotateConfirmMessage',
        'AgentManagement::Sip:RotateConfirmTitle',
        {
          hideCancelBtn: false,
          hideYesBtn: false,
          dismissible: true,
          yesText: 'AgentManagement::Sip:Rotate',
          cancelText: 'AbpUi::Cancel',
        }
      )
      .pipe(
        switchMap(status => {
          if (status !== Confirmation.Status.confirm) {
            return EMPTY;
          }

          this.rotating.set(true);
          return this.sipCredentialService
            .rotate(userId)
            .pipe(finalize(() => this.rotating.set(false)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: credentials => {
          this.credentials.set(credentials);
          this.toaster.success('AgentManagement::Sip:Rotated');
        },
        error: () => this.toaster.error('AgentManagement::Sip:RotateFailed'),
      });
  }

  copy(value?: string | null): void {
    if (!value || !navigator?.clipboard) {
      return;
    }

    navigator.clipboard.writeText(value).then(() => {
      this.toaster.success('AgentManagement::Sip:Copied');
    });
  }
}
